import type { HomePageData } from "../../services/HomeService";
import { Card, CardContent } from "../ui/card";
import { cn } from "../../lib/utils";
import {
  gluceraHintNano,
  gluceraListItem,
  gluceraMicroLabel,
} from "../../utils/gluceraTypography";

type PredictionItem = NonNullable<
  HomePageData["Powering This Prediction"]
>[number];

interface HomePoweringPredictionListProps {
  items: PredictionItem[];
  headerLabel: string;
  headerHint?: string;
  onRecalibrate?: () => void;
}

const HomePoweringPredictionList = ({
  items,
  headerLabel,
  headerHint,
  onRecalibrate,
}: HomePoweringPredictionListProps) => {
  if (items.length === 0) return null;

  return (
    <Card className="rounded-2xl border border-border/70 bg-card shadow-sm">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <span className={gluceraMicroLabel}>{headerLabel}</span>
          {headerHint ? (
            <span className={cn("max-w-[9rem] text-right", gluceraHintNano)}>
              {headerHint}
            </span>
          ) : null}
        </div>
        <ul className={`space-y-3 ${gluceraListItem}`}>
          {items.map((item, idx) => {
            const isStale = item.meta?.toLowerCase().includes("days ago") ?? false;
            const isFingerPrick = item.title.toLowerCase().includes("finger");
            const dotColor = isStale ? "bg-orange-500" : "bg-emerald-500";
            return (
              <li
                key={idx}
                className="flex flex-wrap items-center gap-x-1.5 gap-y-1"
              >
                <span className={`h-2 w-2 shrink-0 rounded-full ${dotColor}`} />
                <span className="text-muted-foreground">{item.title}</span>
                <span className="text-foreground">· {item.value}</span>
                {item.meta ? (
                  <span className="text-muted-foreground">· {item.meta}</span>
                ) : null}
                {isFingerPrick && isStale && onRecalibrate ? (
                  <button
                    type="button"
                    onClick={onRecalibrate}
                    className="text-orange-600 underline-offset-2 hover:underline dark:text-orange-400"
                  >
                    Recalibrate
                  </button>
                ) : isFingerPrick && isStale ? (
                  <span className="text-orange-600 dark:text-orange-400">
                    Recalibrate
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
};

export default HomePoweringPredictionList;
